import {
  Autocomplete,
  Box,
  Button,
  Divider,
  IconButton,
  Modal,
  Stack,
  Typography,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { StyledCreateTrainingPopup } from '../../styles/TrainingsStyle/TrainingsStyle';
import {
  StyledDatePicker,
  StyledTextArea,
  StyledTextField,
} from '../../styles/FieldsStyles';
import { ReactComponent as BorderX } from '../../assets/images/main-page/border-x-icon.svg';
import { ReactComponent as Calendar } from '../../assets/images/main-page/calendar-icon.svg';
import { ReactComponent as Trash } from '../../assets/images/main-page/trash-icon.svg';
import { StyledEmployeesWrapper } from '../../styles/WorkArrangement/CreateArrangementStyle';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { createProject, updateProject } from '../../api/projectsApi';
import { fetchClients } from '../../api/clientApi';
import { fetchLocations } from '../../api/locationApi';
import { fetchSingleProjects } from '../../api/projectsApi';
import { fetchInspectorsByClient } from '../../api/inspectorApi';

const DEFAULT_NEW_PROJECT = {
  name: '',
  discreption: '',
  client_uuid: '',
  s_date: '',
  e_date: '',
  worth_of_lunch: '',
  worth_of_dinner: '',
  area: '',
  location_uuid: '',
  address: '',
  agreement_no: '',
  insurances: [
    {
      insurance_name: '',
      from_date: '',
      to_date: '',
      policy_number: '',
    },
  ],
  ProjectInspectors: [
    {
      role: '',
      name: '',
      tell: '',
      email: '',
      officePhone: '',
    },
  ],
};

function CreateProjectModal({
  showCreateModal,
  setShowCreateModal,
  createProjectMutation,
  updateProjectMutation,
  newProject = DEFAULT_NEW_PROJECT,
  setNewProject = () => {},
  editMode = false,
  editProjectUuid,
  setEditMode = () => {},
  clientUuid,
}) {
  const [fieldErrors, setFieldErrors] = useState({});
  const [localProject, setLocalProject] = useState(
    newProject || DEFAULT_NEW_PROJECT,
  );
  const queryClient = useQueryClient();

  // unified setter: always update local state, and also call parent setter if provided
  const setProject = (updated) => {
    setLocalProject(updated);
    try {
      setNewProject(updated);
    } catch (e) {
      // ignore if parent setter is not provided or throws
    }
  };

  useEffect(() => {
    if (showCreateModal) {
      if (editMode) {
        // Let the edit useEffect handle it
      } else {
        setProject({
          ...(newProject || DEFAULT_NEW_PROJECT),
          client_uuid: clientUuid || newProject?.client_uuid || '',
        });
      }
    }
  }, [showCreateModal, editMode, clientUuid, newProject]);

  const validateForm = () => {
    const errors = {};
    if (!localProject?.name || !String(localProject.name).trim()) {
      errors.name = t('validation.name');
    }
    if (!localProject?.s_date) {
      errors.s_date = t('validation.s_date');
    }
    if (localProject?.s_date && localProject?.e_date) {
      try {
        if (dayjs(localProject.s_date).isAfter(dayjs(localProject.e_date))) {
          errors.e_date = t('validation.e_date_order');
        }
      } catch (e) {
        // ignore parse errors here; server will validate
      }
    }
    if (!localProject?.location_uuid) {
      errors.location_uuid = t('validation.location_uuid');
    }
    if (
      !localProject?.agreement_no ||
      !String(localProject.agreement_no).trim()
    ) {
      errors.agreement_no = t('validation.agreement_no');
    }
    if (!localProject?.address || !String(localProject.address).trim()) {
      errors.address = t('validation.address');
    }
    // meal rates validation
    if (
      !localProject?.worth_of_lunch ||
      !String(localProject.worth_of_lunch).trim()
    ) {
      errors.worth_of_lunch = t('validation.worth_of_lunch');
    }
    if (
      !localProject?.worth_of_dinner ||
      !String(localProject.worth_of_dinner).trim()
    ) {
      errors.worth_of_dinner = t('validation.worth_of_dinner');
    }

    // client required
    if (!localProject?.client_uuid) {
      errors.client_uuid = t('validation.client_uuid');
    }

    // insurances validation (per-item)
    if (
      Array.isArray(localProject?.insurances) &&
      localProject.insurances.length
    ) {
      const insErrors = [];
      localProject.insurances.forEach((ins, i) => {
        const ie = {};
        if (!ins?.insurance_name || !String(ins.insurance_name).trim()) {
          ie.insurance_name = t('validation.insurance_name');
        }
        if (!ins?.from_date) {
          ie.from_date = t('validation.insurance_from_date');
        }
        if (!ins?.to_date) {
          ie.to_date = t('validation.insurance_to_date');
        }
        if (ins?.from_date && ins?.to_date) {
          try {
            if (dayjs(ins.from_date).isAfter(dayjs(ins.to_date))) {
              ie.to_date = t('validation.insurance_to_date_order');
            }
          } catch (e) {
            // ignore
          }
        }
        if (!ins?.policy_number || !String(ins.policy_number).trim()) {
          ie.policy_number = t('validation.insurance_policy_number');
        }
        insErrors[i] = Object.keys(ie).length ? ie : null;
      });
      // only set if any item has errors
      if (insErrors.some((it) => it)) errors.insurances = insErrors;
    }

    // contacts (ProjectInspectors) validation (per-item)
    if (
      Array.isArray(localProject?.ProjectInspectors) &&
      localProject.ProjectInspectors.length
    ) {
      const contactErrors = [];
      localProject.ProjectInspectors.forEach((c, i) => {
        const ce = {};
        if (!c?.name || !String(c.name).trim()) {
          ce.name = t('validation.contact_name');
        }
        // email is optional; only validate format if a value is provided
        if (c?.email && String(c.email).trim()) {
          const emailPattern = /\S+@\S+\.\S+/;
          if (!emailPattern.test(c.email)) {
            ce.email = t('validation.contact_email_invalid') || 'Invalid email';
          }
        }

        // tell (phone) should be digits only per server rule /^\d+$/
        if (!c?.tell) {
          const tellPattern = /^\d+$/;
          if (!tellPattern.test(String(c.tell))) {
            ce.tell = t('validation.contact_tell_numeric');
          }
        }

        if (!c?.role || !String(c.role).trim()) {
          ce.role = t('validation.contact_role');
        }

        contactErrors[i] = Object.keys(ce).length ? ce : null;
      });
      if (contactErrors.some((it) => it))
        errors.ProjectInspectors = contactErrors;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const {
    data: clientList,
    error: clientListError,
    status: clientListStatus,
  } = useQuery('clientlist-dropdown', () => fetchClients({ pagination: 0 }), {
    refetchOnWindowFocus: false,
    keepPreviousData: false,
    onError: console.log,
  });

  const {
    data: locationList,
    error: locationListError,
    status: locationListStatus,
  } = useQuery(
    'locationlist-dropdown',
    () => fetchLocations({ pagination: 0 }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      onError: console.log,
    },
  );

  const { data: projectData } = useQuery(
    ['single-project', editProjectUuid],
    fetchSingleProjects,
    {
      enabled: editMode && !!editProjectUuid,
      refetchOnWindowFocus: false,
      onError: console.log,
    },
  );

  // Fetch inspectors for the currently selected client
  const { data: clientInspectorsData } = useQuery(
    ['inspectors-by-client', localProject?.client_uuid],
    () => fetchInspectorsByClient(localProject.client_uuid),
    {
      enabled: !!localProject?.client_uuid,
      refetchOnWindowFocus: false,
      onError: console.log,
    },
  );

  const clientInspectors = clientInspectorsData?.data || [];

  // Auto-add the single inspector when there is exactly one for the selected client
  React.useEffect(() => {
    if (clientInspectors.length === 1 && localProject?.client_uuid) {
      const insp = clientInspectors[0];
      const alreadyAdded = (localProject.ProjectInspectors || []).some(
        (c) => c.email && c.email === insp.email,
      );
      if (!alreadyAdded) {
        // Replace blank placeholder row if it is the only empty entry
        const currentContacts = localProject.ProjectInspectors || [];
        const isOnlyBlank =
          currentContacts.length === 1 &&
          !currentContacts[0].name &&
          !currentContacts[0].email &&
          !currentContacts[0].tell;
        const updated = isOnlyBlank
          ? [
              {
                name: insp.name,
                email: insp.email,
                tell: insp.tell || '',
                officePhone: insp.officePhone || '',
                role: insp.role || '',
                uuid: insp.uuid,
              },
            ]
          : [
              ...currentContacts,
              {
                name: insp.name,
                email: insp.email,
                tell: insp.tell || '',
                officePhone: insp.officePhone || '',
                role: insp.role || '',
                uuid: insp.uuid,
              },
            ];
        setProject({ ...localProject, ProjectInspectors: updated });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientInspectors, localProject?.client_uuid]);

  // internal mutations if parent doesn't provide them (CustomerManagement opens modal without passing mutations)
  const internalCreateProjectMutation = useMutation(createProject, {
    onSuccess: (response) => {
      if (response?.status) {
        setFieldErrors({});
        setShowCreateModal(false);
        queryClient.invalidateQueries(['projects']);
      }
    },
    onError: (error) => {
      // keep minimal handling here; the onClick caller will also parse response when available
      console.log('create project error', error);
    },
  });

  const internalUpdateProjectMutation = useMutation(
    ({ uuid, data }) => updateProject(uuid, data),
    {
      onSuccess: (response) => {
        if (response?.status) {
          setFieldErrors({});
          if (typeof setEditMode === 'function') setEditMode(false);
          setShowCreateModal(false);
          queryClient.invalidateQueries(['projects']);
        }
      },
      onError: (error) => {
        console.log('update project error', error);
      },
    },
  );

  React.useEffect(() => {
    if (editMode && projectData?.data) {
      const project = projectData.data;
      console.log('Project data for edit:', project);
      setProject({
        name: project.name || '',
        discreption: project.discreption || '',
        client_uuid: project.client?.uuid || '',
        s_date: project.s_date || '',
        e_date: project.e_date || '',
        worth_of_lunch: String(project.worth_of_lunch || ''),
        worth_of_dinner: String(project.worth_of_dinner || ''),
        area: project.locations?.area || '',
        location_uuid: project.locations?.uuid || '',
        address: project.address || '',
        agreement_no: project.agreement_no || '',
        insurances:
          project.Insurances?.length > 0
            ? project.Insurances.map((ins) => ({
                uuid: ins.uuid,
                insurance_name: ins.insurance_name || '',
                from_date: ins.from_date || '',
                to_date: ins.to_date || '',
                policy_number: ins.policy_number || '',
              }))
            : [
                {
                  insurance_name: '',
                  from_date: '',
                  to_date: '',
                  policy_number: '',
                },
              ],
        ProjectInspectors:
          project.ProjectInspectors?.length > 0
            ? project.ProjectInspectors.map((contact) => ({
                uuid: contact.Inspectors?.uuid,
                role: contact.Inspectors?.role || '',
                name: contact.Inspectors?.name || '',
                tell: contact.Inspectors?.tell || '',
                email: contact.Inspectors?.email || '',
                officePhone: contact.Inspectors?.officePhone || '',
              }))
            : [
                {
                  role: '',
                  name: '',
                  tell: '',
                  email: '',
                  officePhone: '',
                },
              ],
      });
    }
  }, [editMode, projectData]);

  const closeModal = () => {
    setShowCreateModal(false);
    if (typeof setEditMode === 'function') setEditMode(false);
    setFieldErrors({});
  };

  const handleAddInsurance = () => {
    const temp = [...(localProject?.insurances || [])];
    temp.push({
      insurance_name: '',
      from_date: null,
      to_date: null,
      policy_number: '',
    });
    setProject({ ...localProject, insurances: temp });
  };
  const { t } = useTranslation('project');

  const handleChangeInsurance = (index, id, value) => {
    const temp = [...(localProject.insurances || [])];
    temp[index] = { ...(temp[index] || {}), [id]: value };
    setProject({ ...localProject, insurances: temp });
  };

  const handleDeleteInsurance = (index) => {
    let temp = [...(localProject.insurances || [])];
    temp = temp.filter((insurance, i) => i !== index);
    setProject({ ...localProject, insurances: temp });
  };

  const handleAddContact = () => {
    const temp = [...(localProject?.ProjectInspectors || [])];
    temp.push({ name: '', email: '', tell: '', officePhone: '', role: '' });
    setProject({ ...localProject, ProjectInspectors: temp });
  };

  const handleChangeContact = (index, id, value) => {
    const temp = [...(localProject.ProjectInspectors || [])];
    temp[index] = { ...(temp[index] || {}), [id]: value };
    setProject({ ...localProject, ProjectInspectors: temp });
  };

  const handleDeleteContact = (index) => {
    let temp = [...(localProject.ProjectInspectors || [])];
    temp = temp.filter((contact, i) => i !== index);
    setProject({ ...localProject, ProjectInspectors: temp });
  };

  return (
    <Modal
      open={showCreateModal}
      onClose={() => {
        setShowCreateModal(false);
      }}
    >
      <StyledCreateTrainingPopup sx={{ overflow: 'scroll' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          marginBottom="56px"
        >
          <Typography variant="h3">
            {editMode
              ? t('create_titles.edit_project_title', 'ערוך פרויקט')
              : t('create_titles.new_project_title')}
          </Typography>
          <IconButton onClick={closeModal}>
            <BorderX />
          </IconButton>
        </Stack>

        <Stack direction="row" columnGap="24px" marginBottom="20px">
          <Box width="40%">
            <label
              htmlFor="name"
              style={{
                marginBottom: '8px',
                fontWeight: '500',
                display: 'block',
              }}
            >
              {t('create_titles.project_name')}
            </label>
            <StyledTextField
              id="name"
              name="name"
              placeholder={t('create_titles.project_name')}
              value={localProject?.name}
              onChange={(e) => {
                const id = e.target.id;
                setProject({ ...localProject, [id]: e.target.value });
                if (fieldErrors[id]) {
                  const copy = { ...fieldErrors };
                  delete copy[id];
                  setFieldErrors(copy);
                }
              }}
              error={!!fieldErrors.name}
              helperText={fieldErrors.name}
            />
          </Box>
          <Box width="30%">
            <label
              htmlFor="s_date"
              style={{
                marginBottom: '8px',
                fontWeight: '500',
                display: 'block',
              }}
            >
              {t('create_titles.start_date')}
            </label>
            <StyledDatePicker
              id="s_date"
              name="s_date"
              //   label="00/00/00"
              value={localProject?.s_date ? dayjs(localProject.s_date) : null}
              slots={{
                openPickerIcon: Calendar,
              }}
              onChange={(e) =>
                setProject({
                  ...localProject,
                  s_date: dayjs(e.$d).format(),
                })
              }
            />
            {fieldErrors.s_date && (
              <Typography variant="caption" color="error">
                {fieldErrors.s_date}
              </Typography>
            )}
          </Box>
          <Box width="30%">
            <label
              htmlFor="e_date"
              style={{
                marginBottom: '8px',
                fontWeight: '500',
                display: 'block',
              }}
            >
              {t('create_titles.end_date')}
            </label>
            <StyledDatePicker
              id="e_date"
              name="e_date"
              //   label="00/00/00"
              value={localProject?.e_date ? dayjs(localProject.e_date) : null}
              slots={{
                openPickerIcon: Calendar,
              }}
              onChange={(e) =>
                setProject({
                  ...localProject,
                  e_date: dayjs(e.$d).format(),
                })
              }
            />
            {fieldErrors.e_date && (
              <Typography variant="caption" color="error">
                {fieldErrors.e_date}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" columnGap="24px" marginBottom="20px">
          <Box width="30%">
            <label
              htmlFor="agreement_no"
              style={{
                marginBottom: '8px',
                fontWeight: '500',
                display: 'block',
              }}
            >
              {t('create_titles.agreement_number')}
            </label>
            <StyledTextField
              id="agreement_no"
              name="agreement_no"
              placeholder="0 0 0 0 0"
              value={localProject?.agreement_no}
              onChange={(e) => {
                const id = e.target.id;
                setProject({ ...localProject, [id]: e.target.value });
                if (fieldErrors[id]) {
                  const copy = { ...fieldErrors };
                  delete copy[id];
                  setFieldErrors(copy);
                }
              }}
              error={!!fieldErrors.agreement_no}
              helperText={fieldErrors.agreement_no}
            />
          </Box>
          <Box width="30%">
            <label
              htmlFor="area"
              style={{
                marginBottom: '8px',
                fontWeight: '500',
                display: 'block',
              }}
            >
              {t('create_titles.area')}
            </label>
            <Autocomplete
              freeSolo
              options={[
                ...new Set(locationList?.data.map((item) => item.area)),
              ]}
              onChange={(event, value) => {
                setProject({
                  ...localProject,
                  area: value,
                });
              }}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  label={t('create_titles.location')}
                />
              )}
            />
          </Box>
          <Box width="40%">
            <label
              htmlFor="location"
              style={{
                marginBottom: '8px',
                fontWeight: '500',
                display: 'block',
              }}
            >
              {t('create_titles.location')}
            </label>
            <Autocomplete
              freeSolo
              options={locationList?.data || []}
              getOptionLabel={(option) => option.name || ''}
              value={
                locationList?.data?.find(
                  (loc) => loc.uuid === (localProject?.location_uuid || ''),
                ) || null
              }
              onChange={(event, value) => {
                setProject({
                  ...localProject,
                  location_uuid: value?.uuid,
                });
                if (fieldErrors.location_uuid) {
                  const copy = { ...fieldErrors };
                  delete copy.location_uuid;
                  setFieldErrors(copy);
                }
              }}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  label={t('create_titles.location')}
                  error={!!fieldErrors.location_uuid}
                  helperText={fieldErrors.location_uuid}
                />
              )}
            />
          </Box>
        </Stack>

        <Box width="100%" marginBottom="20px">
          <label
            htmlFor="address"
            style={{
              marginBottom: '8px',
              fontWeight: '500',
              display: 'block',
            }}
          >
            {t('create_titles.address')}
          </label>
          <StyledTextField
            id="address"
            name="address"
            placeholder={t('create_titles.address')}
            value={localProject?.address}
            onChange={(e) => {
              const id = e.target.id;
              setProject({ ...localProject, [id]: e.target.value });
              if (fieldErrors[id]) {
                const copy = { ...fieldErrors };
                delete copy[id];
                setFieldErrors(copy);
              }
            }}
            error={!!fieldErrors.address}
            helperText={fieldErrors.address}
          />
        </Box>

        <Box width="100%">
          <label
            htmlFor="discreption"
            style={{
              marginBottom: '8px',
              fontWeight: '500',
              display: 'block',
            }}
          >
            {t('create_titles.work_description')}
          </label>
          <StyledTextArea
            id="discreption"
            name="discreption"
            placeholder={t('create_titles.work_description')}
            value={localProject?.discreption}
            onChange={(e) => {
              setProject({ ...localProject, [e.target.id]: e.target.value });
            }}
          />
          {fieldErrors.discreption && (
            <Typography variant="caption" color="error">
              {fieldErrors.discreption}
            </Typography>
          )}
        </Box>

        <Divider
          sx={{
            backgroundColor: 'rgba(6, 125, 137, 0.3)',
            marginBlock: '32px',
          }}
        />

        <Box>
          <Typography fontWeight="500">
            {t('create_titles.meals_title')}
          </Typography>

          <Stack direction="row" columnGap="32px">
            <Box width="20%">
              <label
                htmlFor="worth_of_lunch"
                style={{
                  marginBottom: '8px',
                  fontWeight: '500',
                  display: 'block',
                }}
              >
                {t('create_titles.lunch_rate')}
              </label>
              <StyledTextField
                id="worth_of_lunch"
                name="worth_of_lunch"
                placeholder='40 ש"ח'
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                inputProps={{ min: 0, step: '1' }}
                value={localProject?.worth_of_lunch ?? ''}
                onChange={(e) => {
                  const id = e.target.id;
                  setProject({ ...localProject, [id]: e.target.value });
                  if (fieldErrors[id]) {
                    const copy = { ...fieldErrors };
                    delete copy[id];
                    setFieldErrors(copy);
                  }
                }}
                error={!!fieldErrors.worth_of_lunch}
                helperText={fieldErrors.worth_of_lunch}
              />
            </Box>
            <Box width="20%">
              <label
                htmlFor="worth_of_dinner"
                style={{
                  marginBottom: '8px',
                  fontWeight: '500',
                  display: 'block',
                }}
              >
                {t('create_titles.dinner_rate')}
              </label>
              <StyledTextField
                id="worth_of_dinner"
                name="worth_of_dinner"
                placeholder="40 ש״ח"
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                inputProps={{ min: 0, step: '1' }}
                value={localProject?.worth_of_dinner ?? ''}
                onChange={(e) => {
                  const id = e.target.id;
                  setProject({ ...localProject, [id]: e.target.value });
                  if (fieldErrors[id]) {
                    const copy = { ...fieldErrors };
                    delete copy[id];
                    setFieldErrors(copy);
                  }
                }}
                error={!!fieldErrors.worth_of_dinner}
                helperText={fieldErrors.worth_of_dinner}
              />
            </Box>
          </Stack>
        </Box>

        <Divider
          sx={{
            backgroundColor: 'rgba(6, 125, 137, 0.3)',
            marginBlock: '32px',
          }}
        />

        <Box>
          <Typography variant="runningText" component="p" marginBottom="16px">
            {t('create_titles.insurances_title')}
          </Typography>

          {localProject?.insurances.map((insurance, index) => {
            return (
              <StyledEmployeesWrapper
                key={index}
                direction="row"
                alignItems="center"
                columnGap="18px"
                marginBottom="32px"
              >
                <Box>
                  <label
                    htmlFor="insurance_name"
                    style={{
                      marginBottom: '8px',
                      fontWeight: '500',
                      display: 'block',
                    }}
                  >
                    {t('create_titles.insurance_name')}
                  </label>
                  <StyledTextField
                    id="insurance_name"
                    name="insurance_name"
                    placeholder={t('create_titles.insurance_name')}
                    value={localProject.insurances[index].insurance_name}
                    onChange={(e) => {
                      handleChangeInsurance(index, e.target.id, e.target.value);
                      // clear per-item error if exists
                      if (fieldErrors.insurances?.[index]?.insurance_name) {
                        const copy = { ...fieldErrors };
                        copy.insurances = copy.insurances || [];
                        copy.insurances[index] = { ...copy.insurances[index] };
                        delete copy.insurances[index].insurance_name;
                        // if object empty, set to null
                        if (
                          copy.insurances[index] &&
                          Object.keys(copy.insurances[index]).length === 0
                        )
                          copy.insurances[index] = null;
                        setFieldErrors(copy);
                      }
                    }}
                    error={!!fieldErrors.insurances?.[index]?.insurance_name}
                    helperText={fieldErrors.insurances?.[index]?.insurance_name}
                  />
                </Box>
                <Box>
                  <label
                    htmlFor="from_date"
                    style={{
                      marginBottom: '8px',
                      fontWeight: '500',
                      display: 'block',
                    }}
                  >
                    {t('create_titles.insurance_start_date')}
                  </label>
                  <StyledDatePicker
                    id="from_date"
                    name="from_date"
                    // label="00/00/00"
                    value={
                      localProject.insurances[index].from_date
                        ? dayjs(localProject.insurances[index].from_date)
                        : null
                    }
                    slots={{
                      openPickerIcon: Calendar,
                    }}
                    onChange={(e) => {
                      handleChangeInsurance(
                        index,
                        'from_date',
                        dayjs(e.$d).format(),
                      );
                      if (fieldErrors.insurances?.[index]?.from_date) {
                        const copy = { ...fieldErrors };
                        copy.insurances = copy.insurances || [];
                        copy.insurances[index] = { ...copy.insurances[index] };
                        delete copy.insurances[index].from_date;
                        if (
                          copy.insurances[index] &&
                          Object.keys(copy.insurances[index]).length === 0
                        )
                          copy.insurances[index] = null;
                        setFieldErrors(copy);
                      }
                    }}
                  />
                  {fieldErrors.insurances?.[index]?.from_date && (
                    <Typography variant="caption" color="error">
                      {fieldErrors.insurances[index].from_date}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <label
                    htmlFor="to_date"
                    style={{
                      marginBottom: '8px',
                      fontWeight: '500',
                      display: 'block',
                    }}
                  >
                    {t('create_titles.insurance_to_date')}
                  </label>
                  <StyledDatePicker
                    id="to_date"
                    name="to_date"
                    // label="00/00/00"
                    value={
                      localProject.insurances[index].to_date
                        ? dayjs(localProject.insurances[index].to_date)
                        : null
                    }
                    slots={{
                      openPickerIcon: Calendar,
                    }}
                    onChange={(e) => {
                      handleChangeInsurance(
                        index,
                        'to_date',
                        dayjs(e.$d).format(),
                      );
                      if (fieldErrors.insurances?.[index]?.to_date) {
                        const copy = { ...fieldErrors };
                        copy.insurances = copy.insurances || [];
                        copy.insurances[index] = { ...copy.insurances[index] };
                        delete copy.insurances[index].to_date;
                        if (
                          copy.insurances[index] &&
                          Object.keys(copy.insurances[index]).length === 0
                        )
                          copy.insurances[index] = null;
                        setFieldErrors(copy);
                      }
                    }}
                  />
                  {fieldErrors.insurances?.[index]?.to_date && (
                    <Typography variant="caption" color="error">
                      {fieldErrors.insurances[index].to_date}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <label
                    htmlFor="policy_number"
                    style={{
                      marginBottom: '8px',
                      fontWeight: '500',
                      display: 'block',
                    }}
                  >
                    {t('create_titles.insurance_policy_number')}
                  </label>
                  <StyledTextField
                    id="policy_number"
                    name="policy_number"
                    placeholder="מספר פוליסה"
                    value={localProject.insurances[index].policy_number}
                    onChange={(e) => {
                      handleChangeInsurance(index, e.target.id, e.target.value);
                      if (fieldErrors.insurances?.[index]?.policy_number) {
                        const copy = { ...fieldErrors };
                        copy.insurances = copy.insurances || [];
                        copy.insurances[index] = { ...copy.insurances[index] };
                        delete copy.insurances[index].policy_number;
                        if (
                          copy.insurances[index] &&
                          Object.keys(copy.insurances[index]).length === 0
                        )
                          copy.insurances[index] = null;
                        setFieldErrors(copy);
                      }
                    }}
                    error={!!fieldErrors.insurances?.[index]?.policy_number}
                    helperText={fieldErrors.insurances?.[index]?.policy_number}
                  />
                </Box>

                <IconButton onClick={() => handleDeleteInsurance(index)}>
                  <Trash />
                </IconButton>
              </StyledEmployeesWrapper>
            );
          })}

          <Button
            variant="borderBlue"
            sx={{ padding: '8px 24px' }}
            onClick={handleAddInsurance}
          >
            {t('create_titles.add_insurance_button')}
          </Button>
        </Box>

        <Divider
          sx={{
            backgroundColor: 'rgba(6, 125, 137, 0.3)',
            marginBlock: '32px',
          }}
        />

        <Box>
          <Box>
            <Typography variant="runningText" component="p" marginBottom="24px">
              {t('create_titles.client_title')}
            </Typography>

            <Typography variant="runningText" component="p" marginBottom="16px">
              {t('create_titles.add_client')}
            </Typography>

            <Autocomplete
              freeSolo
              options={clientList?.data || []}
              getOptionLabel={(option) => option.name || ''}
              value={
                clientList?.data?.find(
                  (client) => client.uuid === (localProject?.client_uuid || ''),
                ) || null
              }
              sx={{
                width: 200,
                marginBottom: '24px',
              }}
              onChange={(event, value) => {
                // When client changes, reset contacts that were auto-added from old client
                setProject({
                  ...localProject,
                  client_uuid: value?.uuid || '',
                  ProjectInspectors: [
                    {
                      role: '',
                      name: '',
                      tell: '',
                      email: '',
                      officePhone: '',
                    },
                  ],
                });
                if (fieldErrors.client_uuid) {
                  const copy = { ...fieldErrors };
                  delete copy.client_uuid;
                  setFieldErrors(copy);
                }
              }}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  label={t('create_titles.add_client')}
                  error={!!fieldErrors.client_uuid}
                  helperText={fieldErrors.client_uuid}
                />
              )}
            />
          </Box>

          {/* Inspector picker — shown when a client is selected and has inspectors */}
          {clientInspectors.length > 1 && (
            <Box marginBottom="20px">
              <Typography
                variant="runningText"
                component="p"
                marginBottom="8px"
                fontWeight="500"
              >
                {t(
                  'create_titles.add_inspector_from_client',
                  'בחר מפקח מהלקוח',
                )}
              </Typography>
              <Autocomplete
                options={clientInspectors}
                getOptionLabel={(opt) =>
                  `${opt.name}${opt.tell ? ' | ' + opt.tell : ''}${opt.email ? ' | ' + opt.email : ''}`
                }
                onChange={(event, selected) => {
                  if (!selected) return;
                  const alreadyAdded = (
                    localProject.ProjectInspectors || []
                  ).some((c) => c.email && c.email === selected.email);
                  if (alreadyAdded) return;
                  const currentContacts = localProject.ProjectInspectors || [];
                  const isOnlyBlank =
                    currentContacts.length === 1 &&
                    !currentContacts[0].name &&
                    !currentContacts[0].email &&
                    !currentContacts[0].tell;
                  const updated = isOnlyBlank
                    ? [
                        {
                          name: selected.name,
                          email: selected.email,
                          tell: selected.tell || '',
                          officePhone: selected.officePhone || '',
                          role: selected.role || '',
                          uuid: selected.uuid,
                        },
                      ]
                    : [
                        ...currentContacts,
                        {
                          name: selected.name,
                          email: selected.email,
                          tell: selected.tell || '',
                          officePhone: selected.officePhone || '',
                          role: selected.role || '',
                          uuid: selected.uuid,
                        },
                      ];
                  setProject({ ...localProject, ProjectInspectors: updated });
                }}
                sx={{ width: 350 }}
                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    label={t(
                      'create_titles.add_inspector_from_client',
                      'בחר מפקח מהלקוח',
                    )}
                  />
                )}
              />
            </Box>
          )}

          <Box>
            <Typography variant="runningText" component="p" marginBottom="16px">
              {t('create_titles.contacts_title')}
            </Typography>

            {localProject?.ProjectInspectors.map((contact, index) => {
              return (
                <StyledEmployeesWrapper
                  key={index}
                  direction="row"
                  alignItems="center"
                  columnGap="18px"
                  marginBottom="32px"
                >
                  <Box>
                    <label
                      htmlFor="name"
                      style={{
                        marginBottom: '8px',
                        fontWeight: '500',
                        display: 'block',
                      }}
                    >
                      {t('create_titles.contact_full_name')}
                    </label>
                    <StyledTextField
                      id="name"
                      name="name"
                      placeholder={t('create_titles.contact_full_name')}
                      value={localProject.ProjectInspectors[index].name}
                      onChange={(e) => {
                        handleChangeContact(index, e.target.id, e.target.value);
                        if (fieldErrors.ProjectInspectors?.[index]?.name) {
                          const copy = { ...fieldErrors };
                          copy.ProjectInspectors = copy.ProjectInspectors || [];
                          copy.ProjectInspectors[index] = {
                            ...copy.ProjectInspectors[index],
                          };
                          delete copy.ProjectInspectors[index].name;
                          if (
                            copy.ProjectInspectors[index] &&
                            Object.keys(copy.ProjectInspectors[index])
                              .length === 0
                          )
                            copy.ProjectInspectors[index] = null;
                          setFieldErrors(copy);
                        }
                      }}
                      error={!!fieldErrors.ProjectInspectors?.[index]?.name}
                      helperText={fieldErrors.ProjectInspectors?.[index]?.name}
                    />
                  </Box>
                  <Box>
                    <label
                      htmlFor="email"
                      style={{
                        marginBottom: '8px',
                        fontWeight: '500',
                        display: 'block',
                      }}
                    >
                      {t('create_titles.contact_email')}
                    </label>
                    <StyledTextField
                      id="email"
                      name="email"
                      placeholder={t('create_titles.contact_email')}
                      value={localProject.ProjectInspectors[index].email}
                      onChange={(e) => {
                        handleChangeContact(index, e.target.id, e.target.value);
                        if (fieldErrors.ProjectInspectors?.[index]?.email) {
                          const copy = { ...fieldErrors };
                          copy.ProjectInspectors = copy.ProjectInspectors || [];
                          copy.ProjectInspectors[index] = {
                            ...copy.ProjectInspectors[index],
                          };
                          delete copy.ProjectInspectors[index].email;
                          if (
                            copy.ProjectInspectors[index] &&
                            Object.keys(copy.ProjectInspectors[index])
                              .length === 0
                          )
                            copy.ProjectInspectors[index] = null;
                          setFieldErrors(copy);
                        }
                      }}
                      error={!!fieldErrors.ProjectInspectors?.[index]?.email}
                      helperText={fieldErrors.ProjectInspectors?.[index]?.email}
                    />
                  </Box>
                  <Box>
                    <label
                      htmlFor="tell"
                      style={{
                        marginBottom: '8px',
                        fontWeight: '500',
                        display: 'block',
                      }}
                    >
                      טלפון
                    </label>
                    <StyledTextField
                      id="tell"
                      name="tell"
                      placeholder="טלפון"
                      value={localProject.ProjectInspectors[index].tell}
                      onChange={(e) => {
                        handleChangeContact(index, e.target.id, e.target.value);
                        if (fieldErrors.ProjectInspectors?.[index]?.tell) {
                          const copy = { ...fieldErrors };
                          copy.ProjectInspectors = copy.ProjectInspectors || [];
                          copy.ProjectInspectors[index] = {
                            ...copy.ProjectInspectors[index],
                          };
                          delete copy.ProjectInspectors[index].tell;
                          if (
                            copy.ProjectInspectors[index] &&
                            Object.keys(copy.ProjectInspectors[index])
                              .length === 0
                          )
                            copy.ProjectInspectors[index] = null;
                          setFieldErrors(copy);
                        }
                      }}
                      error={!!fieldErrors.ProjectInspectors?.[index]?.tell}
                      helperText={fieldErrors.ProjectInspectors?.[index]?.tell}
                    />
                  </Box>
                  <Box>
                    <label
                      htmlFor="role"
                      style={{
                        marginBottom: '8px',
                        fontWeight: '500',
                        display: 'block',
                      }}
                    >
                      {t('create_titles.contact_role')}
                    </label>
                    <StyledTextField
                      id="role"
                      name="role"
                      placeholder={t('create_titles.contact_role')}
                      value={localProject.ProjectInspectors[index].role}
                      onChange={(e) => {
                        handleChangeContact(index, e.target.id, e.target.value);
                      }}
                      error={!!fieldErrors.ProjectInspectors?.[index]?.role}
                      helperText={fieldErrors.ProjectInspectors?.[index]?.role}
                    />
                  </Box>

                  <Box>
                    <label
                      htmlFor="officePhone"
                      style={{
                        marginBottom: '8px',
                        fontWeight: '500',
                        display: 'block',
                      }}
                    >
                      טלפון משרד
                    </label>
                    <StyledTextField
                      id="officePhone"
                      name="officePhone"
                      placeholder="טלפון משרד"
                      value={
                        localProject.ProjectInspectors[index].officePhone || ''
                      }
                      onChange={(e) => {
                        handleChangeContact(index, e.target.id, e.target.value);
                      }}
                    />
                  </Box>

                  <IconButton onClick={() => handleDeleteContact(index)}>
                    <Trash />
                  </IconButton>
                </StyledEmployeesWrapper>
              );
            })}
          </Box>

          <Button
            variant="borderBlue"
            sx={{ padding: '8px 24px', marginBottom: '60px' }}
            onClick={handleAddContact}
          >
            {t('create_titles.add_contact_button')}
          </Button>
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="end"
          columnGap="12px"
        >
          <Button
            variant="borderBlue"
            onClick={closeModal}
            sx={{ padding: '7px 15.5px' }}
          >
            {t('create_titles.cancel_button')}
          </Button>
          <Button
            variant="main"
            sx={{ padding: '7px 15.5px' }}
            onClick={() => {
              if (!validateForm()) return;
              const usedCreateMutation =
                createProjectMutation || internalCreateProjectMutation;
              const usedUpdateMutation =
                updateProjectMutation || internalUpdateProjectMutation;

              const sanitizedProject = {
                ...localProject,
                e_date: localProject.e_date === '' ? null : localProject.e_date,
                discreption:
                  localProject.discreption === ''
                    ? null
                    : localProject.discreption,
              };

              if (editMode) {
                usedUpdateMutation.mutate(
                  { uuid: editProjectUuid, data: sanitizedProject },
                  {
                    onError: ({ response }) => {
                      if (!response) return;
                      const status = response.status;
                      const errorMessages = response.data?.data || [];
                      if (status === 400 && Array.isArray(errorMessages)) {
                        const newErrors = {};
                        errorMessages.forEach((err) => {
                          // handle nested array errors like ['ProjectInspectors', 0, 'email']
                          if (Array.isArray(err.path)) {
                            const p = err.path;
                            // common pattern: [ArrayField, index, field]
                            if (
                              p.length >= 3 &&
                              (p[0] === 'ProjectInspectors' ||
                                p[0] === 'insurances' ||
                                p[0] === 'Insurances')
                            ) {
                              const arrKey = p[0];
                              const idx = Number(p[1]);
                              const field = p[2];
                              newErrors[arrKey] = newErrors[arrKey] || [];
                              newErrors[arrKey][idx] =
                                newErrors[arrKey][idx] || {};
                              newErrors[arrKey][idx][field] = err.message;
                              return;
                            }

                            // pattern: [field, subfield]
                            if (p.length === 2) {
                              const key = p[0];
                              const sub = p[1];
                              newErrors[key] = newErrors[key] || {};
                              newErrors[key][sub] = err.message;
                              return;
                            }

                            // fallback to top-level
                            if (p.length === 1) {
                              newErrors[p[0]] = err.message;
                              return;
                            }
                          }

                          // default fallback
                          const key =
                            Array.isArray(err.path) && err.path.length
                              ? err.path[0]
                              : 'general';
                          newErrors[key] = err.message;
                        });
                        setFieldErrors(newErrors);
                      }
                    },
                    onSuccess: () => {
                      setFieldErrors({});
                    },
                  },
                );
              } else {
                usedCreateMutation.mutate(sanitizedProject, {
                  onError: ({ response }) => {
                    if (!response) return;
                    const status = response.status;
                    const errorMessages = response.data?.data || [];
                    if (status === 400 && Array.isArray(errorMessages)) {
                      const newErrors = {};
                      errorMessages.forEach((err) => {
                        if (Array.isArray(err.path)) {
                          const p = err.path;
                          if (
                            p.length >= 3 &&
                            (p[0] === 'ProjectInspectors' ||
                              p[0] === 'insurances' ||
                              p[0] === 'Insurances')
                          ) {
                            const arrKey = p[0];
                            const idx = Number(p[1]);
                            const field = p[2];
                            newErrors[arrKey] = newErrors[arrKey] || [];
                            newErrors[arrKey][idx] =
                              newErrors[arrKey][idx] || {};
                            newErrors[arrKey][idx][field] = err.message;
                            return;
                          }
                          if (p.length === 2) {
                            const key = p[0];
                            const sub = p[1];
                            newErrors[key] = newErrors[key] || {};
                            newErrors[key][sub] = err.message;
                            return;
                          }
                          if (p.length === 1) {
                            newErrors[p[0]] = err.message;
                            return;
                          }
                        }
                        const key =
                          Array.isArray(err.path) && err.path.length
                            ? err.path[0]
                            : 'general';
                        newErrors[key] = err.message;
                      });
                      setFieldErrors(newErrors);
                    }
                  },
                  onSuccess: () => {
                    setFieldErrors({});
                  },
                });
              }
            }}
          >
            {editMode
              ? t('create_titles.update_button', 'עדכן')
              : t('create_titles.add_button')}
          </Button>
        </Stack>
      </StyledCreateTrainingPopup>
    </Modal>
  );
}

export default CreateProjectModal;
